/**
 * Admin SPA entry point — mounts the root App component into the DOM.
 *
 * @module apps/admin/main
 * @see TASK-FRONTEND-SCAFFOLD-VITE-CHG-004
 */
// Self-hosted Inter brand font (bundled, same-origin) — see web/src/theme/fonts.ts
import "../../theme/fonts";
import React from "react";
import ReactDOM from "react-dom/client";
import CssBaseline from "@mui/material/CssBaseline";
import { VerisaThemeProvider } from "../../theme/ThemeProvider";
import { I18nProvider } from "../../i18n/I18nProvider";
import { t } from "@lingui/core/macro";
import { BrandingProvider, useBranding } from "../../branding/BrandingProvider";
import App from "./App";
import { installStaleChunkRecovery } from "../../utils/staleChunkRecovery";

// A lazy chunk from the previous deploy reloads the page once (TASK-BUGFIX-STALE-CHUNK-ERROR-PAGE).
installStaleChunkRecovery();

const root = document.getElementById("root");
if (!root) throw new Error("Root element not found");

/**
 * The admin SPA below the branding provider.
 *
 * It exists because the brand colours are CONTEXT the BrandingProvider publishes:
 * `useBranding()` has to be called BELOW that provider, so the render call cannot
 * read them itself. The value goes straight to `VerisaThemeProvider`'s
 * `brandPalette` prop as literal hex (SPEC-PLATFORM-BRANDING RULE-007,
 * SPEC-FRONTEND-RULE-081); nothing else here may put a colour in the palette.
 */
function AdminRoot(): React.ReactElement {
  const { brandPalette } = useBranding();
  return (
    <VerisaThemeProvider brandPalette={brandPalette}>
      <CssBaseline />
      <App />
    </VerisaThemeProvider>
  );
}

/**
 * The branding mount for this SPA.
 *
 * It is a COMPONENT rather than an element in the render call below because of
 * the suffix: `t` is evaluated where it is written, and `I18nProvider` remounts
 * its subtree on a locale switch (`key={locale}`), so evaluating the macro in a
 * render keeps the document title following the UI language - the same shape
 * `apps/workspace/App.tsx` uses.
 *
 * THE SUFFIX, NEVER A PRODUCT NAME (D-BR-08, RULE-012). The mount owns this
 * app's own word and passes it as a Lingui string; the provider composes
 * "<product name> - Admin" with an ASCII hyphen (RULE-064) the moment the read
 * lands, so a branded installation reads "Varosi AI - Admin" and nothing here
 * carries the vendor name.
 */
function BrandedAdminRoot(): React.ReactElement {
  return (
    /* The one reader of GET /api/v1/branding, ABOVE VerisaThemeProvider and
       outside App's QueryClientProvider (SPEC-PLATFORM-BRANDING UI-004). The
       admin SPA gets the provider and NO footer - RULE-006a mounts the footer
       on the tenant and workspace surfaces only. */
    <BrandingProvider appSuffix={t`Admin`}>
      <AdminRoot />
    </BrandingProvider>
  );
}

ReactDOM.createRoot(root).render(
  <React.StrictMode>
    <I18nProvider>
      <BrandedAdminRoot />
    </I18nProvider>
  </React.StrictMode>,
);
